import type { PageBody, PageCard } from '@/play/pageTypes'
import { registerPages } from '@/play/pageRegistry'

/*
 * Every page, found by folder: each `src/pages/<nnn>-<slug>/` has a `card.ts` that exports `card`
 * (what home shows, imported here with the app) and an `index.ts` that exports `page` (the page
 * itself, its own chunk, loaded when one of its documents opens: `loadPage` in
 * src/play/pageRegistry.ts). Adding a page is adding its folder (`pnpm new-page <slug> "<Title>"`);
 * nothing here changes. Folders starting with `_` (the template) are not pages. Durable events are
 * collected the same way (./events.ts).
 */
const cardModules = import.meta.glob<{ card: PageCard }>(['./*/card.ts', '!./_*/card.ts'], { eager: true })
const bodyModules = import.meta.glob<{ page: PageBody }>(['./*/index.ts', '!./_*/index.ts'])

/** One catalog entry: the page's card, its folder name (`<nnn>-<slug>`) and how to load its body. */
export type CatalogEntry = { folder: string; card: PageCard; load: () => Promise<PageBody> }

const folderOf = (path: string) => path.split('/')[1]
const bodyByFolder = new Map(Object.entries(bodyModules).map(([path, load]) => [folderOf(path), load]))

export const CATALOG: readonly CatalogEntry[] = Object.entries(cardModules)
  .flatMap(([path, mod]): CatalogEntry[] => {
    const folder = folderOf(path)
    const load = bodyByFolder.get(folder)
    if (!mod.card || !load) return []
    return [{ folder, card: mod.card, load: () => load().then((m) => m.page) }]
  })
  .sort((a, b) => a.card.number - b.card.number)

/** Every page's card, in home-screen order (by number). */
export const PAGES: readonly PageCard[] = CATALOG.map((entry) => entry.card)

registerPages(CATALOG)

